/**
 * Photo rotation for variety cards.
 *
 * snippets/card-gallery.liquid renders every photo of a variety product as a
 * slide (whatever the stock "Product card carousel" setting says) and loads
 * this file. It turns the component's own autoplay on, for variety cards only.
 * Dependency-free and no importmap entry, matching assets/variety-gallery.js:
 * if this file never loads, the cards still show photo one and still swipe.
 *
 * Rules:
 * - a card with fewer than two visible slides is left alone;
 * - reduced motion stops it outright - the stock component only makes the
 *   transition instant, it would still advance;
 * - only cards in view rotate, at most MAX_CONCURRENT at a time, staggered so
 *   a grid never flips in lockstep;
 * - hover, a hidden tab and scrolling away pause (the first two are already
 *   wired inside slideshow.js);
 * - a sideways swipe, a mouse or pen press, or an arrow key hands the card
 *   over for good; a finger scrolling the page past a card does not.
 *
 * Opt out with data-card-rotate="off" on the card or any ancestor; change the
 * dwell with data-card-rotate-seconds="N" on the same.
 */

// product-card.liquid marks the same products card-gallery.liquid expands.
const CARD_SELECTOR = 'product-card[data-flower-card]';
const DEFAULT_SECONDS = 9;
const MIN_SECONDS = 5;

/**
 * Six is a little over one desktop row at the 230px grid minimum, so a row in
 * view moves without every card on the page competing for the compositor.
 */
const MAX_CONCURRENT = 6;
const STAGGER_MS = 1500;

const entries = new Map();
let observer = null;

function reducedMotion() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

function setting(card, name) {
  const host = card.closest(`[data-${name}]`);
  return host ? host.getAttribute(`data-${name}`) : '';
}

function dwellSeconds(card) {
  const parsed = parseInt(setting(card, 'card-rotate-seconds'), 10);
  if (Number.isNaN(parsed)) return DEFAULT_SECONDS;
  return Math.max(MIN_SECONDS, parsed);
}

function visibleSlideCount(component) {
  return component.querySelectorAll('slideshow-slide:not([hidden])').length;
}

function stop(entry) {
  if (entry.timer != null) {
    window.clearTimeout(entry.timer);
    entry.timer = null;
  }
  entry.playing = false;
  // pause(), not suspend(): the component's own mouseleave/visibility resume()
  // must not restart a card this file has parked. play() clears it again.
  entry.component.pause();
}

function start(entry, delay) {
  if (entry.taken || entry.playing || entry.timer != null) return;
  entry.timer = window.setTimeout(() => {
    entry.timer = null;
    if (entry.taken || !entry.visible) return;
    entry.component.play();
    entry.playing = true;
  }, delay);
}

/** Decide which in-view cards are allowed to move, nearest the top first. */
function rebalance() {
  const live = [];
  for (const entry of entries.values()) {
    if (entry.taken || !entry.visible) {
      stop(entry);
      continue;
    }
    live.push(entry);
  }

  live.sort((a, b) => a.order - b.order);

  live.forEach((entry, index) => {
    if (index < MAX_CONCURRENT) {
      start(entry, index * STAGGER_MS);
    } else {
      stop(entry);
    }
  });
}

/** A manual move hands the card to the visitor and keeps it there. */
function surrender(entry) {
  if (entry.taken) return;
  entry.taken = true;
  stop(entry);
}

function adopt(card, order) {
  const existing = entries.get(card);
  if (existing) {
    existing.order = order;
    return;
  }

  if (setting(card, 'card-rotate') === 'off') return;

  const component = card.querySelector('slideshow-component');
  if (!component || visibleSlideCount(component) < 2) return;

  const entry = { card, component, order, visible: false, playing: false, taken: false, timer: null };

  // No aria-live: a grid of auto-advancing cards would announce constantly.
  component.setAttribute('autoplay', String(dwellSeconds(card)));

  // trigger 'scroll' also fires for the component's own programmatic scroll,
  // so it can't be treated as a hand on the wheel.
  component.addEventListener('slideshow:select', (event) => {
    if (event.detail?.userInitiated && event.detail.trigger !== 'scroll') surrender(entry);
  });
  // A finger on a card is usually the page scrolling past it, so touch only
  // surrenders on a sideways swipe; a mouse or pen press is deliberate.
  component.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'touch') surrender(entry);
  });
  let touchStart = null;
  component.addEventListener(
    'touchstart',
    (event) => {
      const touch = event.touches[0];
      touchStart = touch ? { x: touch.clientX, y: touch.clientY } : null;
    },
    { passive: true }
  );
  component.addEventListener(
    'touchmove',
    (event) => {
      const touch = event.touches[0];
      if (!touchStart || !touch) return;
      const dx = Math.abs(touch.clientX - touchStart.x);
      const dy = Math.abs(touch.clientY - touchStart.y);
      if (dx > 10 && dx > dy) surrender(entry);
    },
    { passive: true }
  );
  component.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') surrender(entry);
  });

  entries.set(card, entry);
  observer.observe(card);
}

function scan() {
  if (reducedMotion()) return;

  if (!observer) {
    if (typeof IntersectionObserver !== 'function') return;
    observer = new IntersectionObserver(
      (records) => {
        for (const record of records) {
          const entry = entries.get(record.target);
          if (entry) entry.visible = record.isIntersecting;
        }
        rebalance();
      },
      { threshold: 0.25 }
    );
  }

  // Drop cards a morph has taken away, then pick up whatever is there now.
  for (const [card, entry] of entries) {
    if (!card.isConnected) {
      stop(entry);
      observer.unobserve(card);
      entries.delete(card);
    }
  }

  document.querySelectorAll(CARD_SELECTOR).forEach(adopt);
}

function run() {
  customElements.whenDefined('slideshow-component').then(scan);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', run);
} else {
  run();
}

document.addEventListener('visibilitychange', () => {
  if (!document.hidden) window.setTimeout(rebalance, 0);
});

// Turning reduced motion on mid-session must park the cards already running;
// turning it back off picks them up again without a reload.
const motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
motionQuery?.addEventListener?.('change', () => {
  if (motionQuery.matches) {
    for (const [card, entry] of entries) {
      stop(entry);
      observer?.unobserve(card);
    }
    entries.clear();
  } else {
    run();
  }
});

document.addEventListener('shopify:section:load', run);
window.addEventListener('pageshow', run);
